import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Stethoscope, Plus, Search, Loader2, X } from "lucide-react";
import { vendorsApi } from "@/lib/api";
import { useToast } from "@/components/SimpleToast";
import { VendorTile } from "@/components/vendors/VendorTile";
import { VendorQuickAccess } from "@/components/vendors/VendorQuickAccess";
import { VendorFormDialog } from "@/components/vendors/VendorFormDialog";
import { openVendorLink } from "@/components/vendors/VendorLinksContacts";
import { useAuth } from "@/context/AuthContext";

export default function MedicalSoftware() {
  const navigate = useNavigate();
  const toast = useToast();
  const { user } = useAuth();
  const [vendors, setVendors] = useState(null);
  const [quick, setQuick] = useState(null);
  const [search, setSearch] = useState("");
  const [createOpen, setCreateOpen] = useState(false);

  const load = useCallback(async (q) => {
    try {
      const res = await vendorsApi.list(q);
      setVendors([...res.data].sort((a, b) => (a.status === "inactive") - (b.status === "inactive")));
    } catch {
      toast.error("Hersteller konnten nicht geladen werden");
      setVendors([]);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const t = setTimeout(() => load(search.trim()), search ? 300 : 0);
    return () => clearTimeout(t);
  }, [search, load]);
  useEffect(() => { vendorsApi.quickAccess().then((r) => setQuick(r.data)).catch(() => {}); }, []);

  const openLink = (vendorId, link) => {
    openVendorLink(vendorId, link);
    setQuick((q) => q && { ...q, top_links: q.top_links.map((l) => (l.id === link.id ? { ...l, click_count: l.click_count + 1 } : l)) });
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="medical-software-page">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3"><Stethoscope className="w-8 h-8" />Arztsoftware</h1>
          <p className="text-muted-foreground mt-1">Interne Wissens- und Kontaktablage für Softwarehersteller</p>
        </div>
        {user?.role !== "readonly" && <Button onClick={() => setCreateOpen(true)} data-testid="vendor-add-btn"><Plus className="w-4 h-4 mr-2" />Hersteller hinzufügen</Button>}
      </div>

      <div className="relative max-w-xl">
        <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
        <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Hersteller, Links, Notizen, Ansprechpartner, Dokumente durchsuchen…" className="pl-9 pr-9" data-testid="vendor-search-input" />
        {search && (
          <button onClick={() => setSearch("")} className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground" title="Suche leeren" data-testid="vendor-search-clear"><X className="w-4 h-4" /></button>
        )}
      </div>

      {!search && <VendorQuickAccess data={quick} onOpenVendor={(id) => navigate(`/medical-software/${id}`)} onOpenLink={openLink} />}

      {vendors === null ? (
        <div className="py-16 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>
      ) : vendors.length === 0 ? (
        <p className="py-16 text-center text-muted-foreground" data-testid="vendor-list-empty">
          {search ? `Keine Treffer für „${search}“.` : "Noch keine Hersteller angelegt."}
        </p>
      ) : (
        <div className="grid gap-6 md:grid-cols-2" data-testid="vendor-grid">
          {vendors.map((v) => <VendorTile key={v.id} vendor={v} onOpen={() => navigate(`/medical-software/${v.id}`)} />)}
        </div>
      )}

      <VendorFormDialog open={createOpen} onOpenChange={setCreateOpen} onCreated={(v) => { setCreateOpen(false); navigate(`/medical-software/${v.id}`); }} />
    </div>
  );
}
